<template>
  <v-card v-if="!isLoading" flat class="transparent">
    <span v-if="!isLoading">
      <v-card-title>
        <v-row align="center" justify="space-between" no-gutters>
          <v-col class="d-none d-md-block">
            <span class="headline no-split-words">
              {{ $t($route.meta.label) }}
            </span>
          </v-col>
          <v-col class="text-right">
            <v-btn @click="back()">
              <v-icon>mdi-arrow-left</v-icon>
              <span class="d-none d-sm-block"> {{ $t("cancel") }} </span>
            </v-btn>
            <v-btn
              class="success ml-2"
              :disabled="loadingFile"
              @click="save(entity)"
            >
              <v-icon>save</v-icon>
              <span class="d-none d-sm-block"> {{ $t("save") }} </span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-form ref="form">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                class="required"
                dense
                v-model="entity.titulo"
                type="text"
                :rules="[(v) => (v && v.trim() !== '') || $t('error.required')]"
                :label="$t('t_contabilidad.prop.titulo')"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <number-field
                v-model="entity.ano"
                class="required"
                :rules="[(v) => !!v || $t('error.required')]"
                type="integer"
                :label="$t('t_contabilidad.prop.ano')"
              ></number-field>
            </v-col>

            <v-col cols="12" md="4">
              <input
                :ref="'documentFileLoader'"
                type="file"
                hidden
                :accept="extensions.document"
                required
                @change="(e) => updateDocument(e)"
              />
              <v-btn
                color="primary"
                class="my-1"
                small
                @click="chooseDocument()"
              >
                <v-icon>mdi-upload</v-icon>
                <span class="d-none d-sm-block">
                  {{ $t("select_file") }}
                </span>
              </v-btn>
              <span v-if="selectedFile" class="ml-2">
                <v-tooltip
                  v-if="documentViewerTypes.includes(selectedFile.type)"
                  top
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      @click="previewDocument(selectedFile)"
                      v-bind="attrs"
                      v-on="on"
                      ><v-icon color="primary">mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("preview_file") }}</span>
                </v-tooltip>
                <span
                  v-if="documentViewerTypes.includes(selectedFile.type)"
                  class="link"
                  @click="previewDocument(selectedFile)"
                  >{{ selectedFile.name }}</span
                >
                <span v-else>{{ selectedFile.name }}</span>
              </span>
              <span v-else class="ml-2 error--text">
                {{ $t("upload.file_required") }}
              </span>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </span>
    <loading-page v-else></loading-page>

    <document-dialog
      :content="currentDocument"
      :title="currentDocumentTitle"
    ></document-dialog>
  </v-card>
</template>

<script>
import LoadingPage from "@/components/loading-page/LoadingPage.vue";
import checkInvalidID from "@/common/checkInvalidID";

import NumberField from "@/components/number-field/NumberField.vue";
import extensions from "@/common/file-extensions";

import RepositoryFactory from "@/repositories/RepositoryFactory";
import { uploadFile } from "@/common/file-utils";
import DocumentDialog from "@/components/modal_dialog/DocumentDialog.vue";
import documentViewerTypes from "@/enumerates/documentviewertypes";

const ContabilidadEntityRepository = RepositoryFactory.get(
  "ContabilidadEntityRepository"
);

export default {
  name: "contabilidadFormForm",
  components: {
    DocumentDialog,
    LoadingPage,
    NumberField,
  },
  data() {
    return {
      loading: false,
      loadingFile: false,
      entity: {},
      selectedFile: null,
      currentDocument: null,
      currentDocumentTitle: null,
      extensions,
      documentViewerTypes,
    };
  },
  beforeRouteUpdate(to, from, next) {
    // si se accede a la misma ruta con diferentes parámetros, se cargará el nuevo objeto
    if (to.params.id) this._fetchData(to.params.id);
    next();
  },
  created() {
    if (this.$route.params.id) this._fetchData(this.$route.params.id);
  },
  computed: {
    isLoading() {
      return this.loading;
    },
  },
  watch: {},
  methods: {
    _fetchData(id) {
      this.loading = true;
      return ContabilidadEntityRepository.get(id)
        .then((response) => {
          this.entity = response;
          if (this.entity.fichero) {
            ContabilidadEntityRepository.getFile(id).then((savedBlob) => {
              this.selectedFile = new File(
                [savedBlob],
                this.entity.fichero.fileName,
                { type: savedBlob.type }
              );
            });
          }
        })
        .catch((err) => checkInvalidID(err))
        .finally(() => (this.loading = false));
    },
    back() {
      this.$router.push({
        name: "Contabilidad List",
        params: { backAction: true },
      });
    },
    async save() {
      if (!this.$refs.form.validate() || !this.selectedFile) {
        this.$notify({
          type: "error",
          text: this.$t("t_contabilidad.error.form-errors"),
        });
        return;
      }

      // Rename original document
      this.selectedFile = new File(
        [this.selectedFile],
        this.entity.titulo + this.selectedFile.name.match(/\.[^/.]+$/)[0]
      );

      try {
        if (this.selectedFile) {
          this.entity.fichero = await uploadFile(this.selectedFile);
        }
      } catch (e) {
        console.error(e);
        this.selectedFile = null;
      }

      this.loading = true;
      ContabilidadEntityRepository.save(this.entity)
        .then(() => this.$router.push({ name: "Contabilidad List" }))
        .finally(() => (this.loading = false));
    },
    chooseDocument() {
      this.$refs.documentFileLoader.click();
    },
    updateDocument(file) {
      if (!file.target.files[0]) return;
      this.selectedFile = file.target.files[0];

      // Remove file extension from title
      this.entity.titulo = this.selectedFile.name.replace(/\.[^/.]+$/, "");
    },
    previewDocument(item) {
      this.currentDocument = new Promise((resolve) => {
        resolve(new Blob([item], { type: item.type }));
      });
      this.currentDocumentTitle = item.name;
    },
  },
};
</script>
